import { Vue } from 'vue-property-decorator'
import {
  ResponseQuestion,
  ResponseQuestionCorrectInput,
  ResponseQuestionCorrectNumericalFormula,
  ResponseQuestionCorrectJapanese,
  ResponseQuestionCorrectEnglish,
} from '@/api/responses/problems'
import { DrawingModel } from '@/models/DrawingModel'

/**
 * 問題情報に関するフロント用モデル定義
 *   小問情報からStudyModel.Problemを生成
 *   生成した小問情報に設問情報を追加していく
 */
export namespace StudyModel {
  // 解答タイプ定義
  export const ANSWER_FORMAT_CODE = {
    // 例題
    EXAMPLE: 'EXAMPLE',
    // 入力式
    INPUT: 'INPUT',
    // 数式入力
    NUMERICAL_FORMULA: 'NUMERICAL_FORMULA',
    // 日本語(手書き)
    JAPANESE: 'JAPANESE',
    // 英語(手書き)
    ENGLISH: 'ENGLISH',
    // 択一式
    SINGLE_CHOICE: 'SINGLE_CHOICE',
  }

  // 手書き入力モード定義
  export const DRAWING_INPUT_MODE = {
    // 手書き入力
    PEN: 'PEN',
    // キーボード入力
    KEYBOARD: 'KEYBOARD',
  }

  /**
   * 解答情報を保持する基底インターフェース
   *   このインターフェースを継承し、解答形式ごとにクラスを定義する
   */
  interface ContentInterface {
    /** フロント使用する形に合わせて値を返すgetter */
    readonly frontValues: FrontValues
    /** 正答文字列(選択式など、解答形式の拡張に備えてnull許容) */
    readonly correctText: string | null
  }

  /**
   * フロント使用する情報の基底型
   *   この型を継承し、解答形式ごとにフロント使用する情報の型を定義する
   */
  type FrontValues = object

  /**
   * 解答情報-例題
   */
  export class ContentExample implements ContentInterface {
    /**
     * フロント使用する値を返す
     *
     * @returns フロント使用する値
     */
    public get frontValues(): FrontValueExample {
      return {}
    }

    /**
     * 正答の文字列を返す(例題では使用しない)
     *
     * @returns null(固定値)
     */
    public get correctText(): null {
      return null
    }
  }

  /**
   * フロントで使用する値-例題
   */
  export type FrontValueExample = FrontValues

  /**
   * 解答情報-入力式
   */
  export class ContentInput implements ContentInterface {
    /** 入力欄前の固定文字列 */
    readonly beforeText: string
    /** 入力欄後の固定文字列 */
    readonly afterText: string
    /** 正答の文字列 */
    readonly correctInputText: string

    /**
     * コンストラクタ
     *
     * @param beforeText 入力欄前の固定文字列
     * @param afterText 入力欄後の固定文字列
     * @param correctInputText 正答の文字列
     */
    constructor(beforeText: string | null, afterText: string | null, correctInputText: string | null) {
      this.beforeText = beforeText || ''
      this.afterText = afterText || ''
      this.correctInputText = correctInputText || ''
    }

    /**
     * フロント使用する値を返す
     *
     * @returns フロント使用する値
     */
    public get frontValues(): FrontValueInput {
      return {
        beforeText: this.beforeText,
        afterText: this.afterText,
        correctText: this.correctText,
      }
    }

    /**
     * 正答の文字列を返す
     *
     * @returns 正答の文字列
     */
    public get correctText(): string {
      return this.correctInputText
    }
  }

  /**
   * フロントで使用する値-入力式
   */
  export type FrontValueInput = FrontValues & {
    /** 入力欄前の固定文字列 */
    beforeText: string
    /** 入力欄後の固定文字列 */
    afterText: string
    /** 正答の文字列 */
    correctText: string
  }

  /**
   * 入力後のメソッド型定義-入力式
   */
  export type AfterInputFunction = (value: string) => void

  /**
   * 解答情報-数式
   */
  export class ContentNumericalFormula implements ContentInterface {
    /** 入力欄前の固定文字列 */
    readonly beforeText: string
    /** 入力欄後の固定文字列 */
    readonly afterText: string
    /** 正答の文字列 */
    readonly correctInputText: string
    /** 入力モード */
    inputType: string
    /** ストローク情報 */
    strokes: DrawingModel.Stroke[]

    /**
     * コンストラクタ
     *
     * @param beforeText 入力欄前の固定文字列
     * @param afterText 入力欄後の固定文字列
     * @param correctInputText 正答の文字列
     */
    constructor(beforeText: string | null, afterText: string | null, correctInputText: string | null) {
      this.beforeText = beforeText || ''
      this.afterText = afterText || ''
      this.correctInputText = correctInputText || ''
      // デフォルトの入力モード設定
      this.inputType = DRAWING_INPUT_MODE.PEN
      this.strokes = []
    }

    /**
     * フロント使用する値を返す
     *
     * @returns フロント使用する値
     */
    public get frontValues(): FrontValueNumericalFormula {
      return {
        beforeText: this.beforeText,
        afterText: this.afterText,
        correctText: this.correctText,
      }
    }

    /**
     * 正答の文字列を返す
     *
     * @returns 正答の文字列
     */
    public get correctText(): string {
      return this.correctInputText
    }
  }

  /**
   * フロントで使用する値-数式
   */
  export type FrontValueNumericalFormula = FrontValues & {
    /** 入力欄前の固定文字列 */
    beforeText: string
    /** 入力欄後の固定文字列 */
    afterText: string
    /** 正答の文字列 */
    correctText: string
  }

  /**
   * 入力後のメソッド型定義-数式
   */
  export type AfterInputNumericalFormulaFunction = (
    value: string,
    inputType: string,
    strokes: DrawingModel.Stroke[]
  ) => void

  /**
   * 解答情報-日本語(手書き)
   */
  export class ContentJapanese implements ContentInterface {
    /** 入力欄前の固定文字列 */
    readonly beforeText: string
    /** 入力欄後の固定文字列 */
    readonly afterText: string
    /** 正答の文字列 */
    readonly correctInputText: string
    /** 入力モード */
    inputType: string
    /** ストローク情報 */
    strokes: DrawingModel.Stroke[]

    /**
     * コンストラクタ
     *
     * @param beforeText 入力欄前の固定文字列
     * @param afterText 入力欄後の固定文字列
     * @param correctInputText 正答の文字列
     */
    constructor(beforeText: string | null, afterText: string | null, correctInputText: string | null) {
      this.beforeText = beforeText || ''
      this.afterText = afterText || ''
      this.correctInputText = correctInputText || ''
      // デフォルトの入力モード設定
      this.inputType = DRAWING_INPUT_MODE.PEN
      this.strokes = []
    }

    /**
     * フロント使用する値を返す
     *
     * @returns フロント使用する値
     */
    public get frontValues(): FrontValueNumericalFormula {
      return {
        beforeText: this.beforeText,
        afterText: this.afterText,
        correctText: this.correctText,
      }
    }

    /**
     * 正答の文字列を返す
     *
     * @returns 正答の文字列
     */
    public get correctText(): string {
      return this.correctInputText
    }
  }

  /**
   * フロントで使用する値-日本語(手書き)
   */
  export type FrontValueJapanese = FrontValues & {
    /** 入力欄前の固定文字列 */
    beforeText: string
    /** 入力欄後の固定文字列 */
    afterText: string
    /** 正答の文字列 */
    correctText: string
  }

  /**
   * 入力後のメソッド型定義-日本語(手書き)
   */
  export type AfterInputJapaneseFunction = (value: string, inputType: string, strokes: DrawingModel.Stroke[]) => void

  /**
   * 解答情報-英語(手書き)
   */
  export class ContentEnglish implements ContentInterface {
    /** 入力欄前の固定文字列 */
    readonly beforeText: string
    /** 入力欄後の固定文字列 */
    readonly afterText: string
    /** 正答の文字列 */
    readonly correctInputText: string
    /** 入力モード */
    inputType: string
    /** ストローク情報 */
    strokes: DrawingModel.Stroke[]

    /**
     * コンストラクタ
     *
     * @param beforeText 入力欄前の固定文字列
     * @param afterText 入力欄後の固定文字列
     * @param correctInputText 正答の文字列
     */
    constructor(beforeText: string | null, afterText: string | null, correctInputText: string | null) {
      this.beforeText = beforeText || ''
      this.afterText = afterText || ''
      this.correctInputText = correctInputText || ''
      // デフォルトの入力モード設定
      this.inputType = DRAWING_INPUT_MODE.PEN
      this.strokes = []
    }

    /**
     * フロント使用する値を返す
     *
     * @returns フロント使用する値
     */
    public get frontValues(): FrontValueEnglish {
      return {
        beforeText: this.beforeText,
        afterText: this.afterText,
        correctText: this.correctText,
      }
    }

    /**
     * 正答の文字列を返す
     *
     * @returns 正答の文字列
     */
    public get correctText(): string {
      return this.correctInputText
    }
  }

  /**
   * フロントで使用する値-英語(手書き)
   */
  export type FrontValueEnglish = FrontValues & {
    /** 入力欄前の固定文字列 */
    beforeText: string
    /** 入力欄後の固定文字列 */
    afterText: string
    /** 正答の文字列 */
    correctText: string
  }

  /**
   * 入力後のメソッド型定義-英語(手書き)
   */
  export type AfterInputEnglishFunction = (value: string, inputType: string, strokes: DrawingModel.Stroke[]) => void

  /**
   * 解答情報-択一式
   */
  export class ContentSingleChoice implements ContentInterface {
    /** 入力欄前の固定文字列 */
    readonly beforeText: string
    /** 入力欄後の固定文字列 */
    readonly afterText: string
    /** 正答の文字列 */
    readonly correctValue: string
    /** 選択肢の文字列リスト */
    readonly optionValues: string[]

    /**
     * コンストラクタ
     *
     * @param beforeText 入力欄前の固定文字列
     * @param afterText 入力欄後の固定文字列
     * @param optionValues 選択肢の文字列リスト
     * @param correctValue 正答の文字列
     */
    constructor(
      beforeText: string | null,
      afterText: string | null,
      optionValues: string[],
      correctValue: string | null
    ) {
      this.beforeText = beforeText || ''
      this.afterText = afterText || ''
      this.optionValues = optionValues
      this.correctValue = correctValue || ''
    }

    /**
     * フロント使用する値を返す
     *
     * @returns フロント使用する値
     */
    public get frontValues(): FrontValueSingleChoice {
      return {
        beforeText: this.beforeText,
        afterText: this.afterText,
        optionValues: this.optionValues,
        correctText: this.correctText,
      }
    }

    /**
     * 正答の文字列を返す
     *
     * @returns 正答の文字列
     */
    public get correctText(): string {
      return this.correctValue
    }
  }

  /**
   * フロントで使用する値-択一式
   */
  export type FrontValueSingleChoice = FrontValues & {
    /** 入力欄前の固定文字列 */
    beforeText: string
    /** 入力欄後の固定文字列 */
    afterText: string
    /** 正答の文字列 */
    correctText: string
    /** 選択肢の文字列リスト */
    optionValues: string[]
  }

  /**
   * 入力後のメソッド型定義-択一式
   */
  export type AfterInputSingleChoiceFunction = (value: string) => void

  /**
   * 設問情報を保持するクラス
   */
  export class Question {
    /** 設問コード */
    readonly questionCode: string
    /** 設問番号(連番) */
    readonly questionNumber: number
    /** 設問ラベル */
    readonly managementLabel: string
    /** 解答タイプ */
    readonly answerFormatCode: string
    /** 解答情報 */
    contents: ContentInterface[]
    /** 入力情報(画面入力値を受け取るための変数) */
    inputValues: string[]
    /** 正誤結果 */
    readonly isCorrect?: boolean
    /** 設問ID */
    id: number

    /**
     * 共通コンストラクタ(実際には問題タイプごとのラッパーメソッドで生成する)
     *
     * @param questionCode 設問コード
     * @param questionNumber 設問番号(連番)
     * @param managementLabel 設問ラベル
     * @param answerFormatCode 解答タイプ
     * @param isCorrect 正誤結果
     */
    private constructor(
      questionCode: string,
      questionNumber: number,
      managementLabel: string | null,
      answerFormatCode: string,
      isCorrect?: boolean
    ) {
      this.questionCode = questionCode
      this.questionNumber = questionNumber
      // ラベルが存在しない場合は連番をラベルとして使用する
      this.managementLabel = managementLabel || `${questionNumber}`
      this.answerFormatCode = answerFormatCode
      this.isCorrect = isCorrect
      this.contents = []
      this.inputValues = []
      // IDはAPIレスポンスから生成した場合のみ後から設定する
      this.id = 0
    }

    /**
     * 例題の問題情報インスタンスを生成する
     *
     * @param questionCode 設問コード
     * @param questionNumber 設問番号(連番)
     * @param isCorrect 正誤結果
     * @returns 問題情報インスタンス
     */
    public static createExampleQuestion(questionCode: string, questionNumber: number, isCorrect?: boolean): Question {
      // インスタンス生成
      const question = new Question(questionCode, questionNumber, null, ANSWER_FORMAT_CODE.EXAMPLE, isCorrect)
      // 互換性のため、1件だけ解答情報と入力枠を生成する
      question.contents.push(new ContentExample())
      return question
    }

    /**
     * 数式の問題情報インスタンスを生成する
     *
     * @param questionCode 設問コード
     * @param questionNumber 設問番号(連番)
     * @param managementLabel 設問ラベル
     * @param inputSize 入力欄の数
     * @param beforeTexts 入力欄前の固定文字列(配列)
     * @param afterTexts 入力欄後の固定文字列(配列)
     * @param correctTexts 正解文字列(配列)
     * @param isCorrect 正誤結果
     * @returns 問題情報インスタンス
     */
    public static createNumericalFormulaQuestion(
      questionCode: string,
      questionNumber: number,
      managementLabel: string | null,
      inputSize: number,
      beforeTexts: (string | null)[],
      afterTexts: (string | null)[],
      correctTexts?: string[],
      isCorrect?: boolean
    ): Question {
      // インスタンス生成
      const question = new Question(
        questionCode,
        questionNumber,
        managementLabel,
        ANSWER_FORMAT_CODE.NUMERICAL_FORMULA,
        isCorrect
      )
      // 解答欄の件数分だけ解答情報と入力枠を生成する
      for (let i = 0; i < inputSize; i++) {
        const correctText = correctTexts ? correctTexts[i] : null
        question.contents.push(new ContentNumericalFormula(beforeTexts[i], afterTexts[i], correctText))
        question.inputValues.push('')
      }
      return question
    }

    /**
     * 入力式の問題情報インスタンスを生成する
     *
     * @param questionCode 設問コード
     * @param questionNumber 設問番号(連番)
     * @param managementLabel 設問ラベル
     * @param inputSize 入力欄の数
     * @param beforeTexts 入力欄前の固定文字列(配列)
     * @param afterTexts 入力欄後の固定文字列(配列)
     * @param correctTexts 正解文字列(配列)
     * @param isCorrect 正誤結果
     * @returns 問題情報インスタンス
     */
    public static createInputQuestion(
      questionCode: string,
      questionNumber: number,
      managementLabel: string | null,
      inputSize: number,
      beforeTexts: (string | null)[],
      afterTexts: (string | null)[],
      correctTexts?: string[],
      isCorrect?: boolean
    ): Question {
      // インスタンス生成
      const question = new Question(questionCode, questionNumber, managementLabel, ANSWER_FORMAT_CODE.INPUT, isCorrect)
      // 解答欄の件数分だけ解答情報と入力枠を生成する
      for (let i = 0; i < inputSize; i++) {
        const correctText = correctTexts ? correctTexts[i] : null
        question.contents.push(new ContentInput(beforeTexts[i], afterTexts[i], correctText))
        question.inputValues.push('')
      }
      return question
    }

    /**
     * 日本語(手書き)の問題情報インスタンスを生成する
     *
     * @param questionCode 設問コード
     * @param questionNumber 設問番号(連番)
     * @param managementLabel 設問ラベル
     * @param inputSize 入力欄の数
     * @param beforeTexts 入力欄前の固定文字列(配列)
     * @param afterTexts 入力欄後の固定文字列(配列)
     * @param correctTexts 正解文字列(配列)
     * @param isCorrect 正誤結果
     * @returns 問題情報インスタンス
     */
    public static createJapaneseQuestion(
      questionCode: string,
      questionNumber: number,
      managementLabel: string | null,
      inputSize: number,
      beforeTexts: (string | null)[],
      afterTexts: (string | null)[],
      correctTexts?: string[],
      isCorrect?: boolean
    ): Question {
      // インスタンス生成
      const question = new Question(
        questionCode,
        questionNumber,
        managementLabel,
        ANSWER_FORMAT_CODE.JAPANESE,
        isCorrect
      )
      // 解答欄の件数分だけ解答情報と入力枠を生成する
      for (let i = 0; i < inputSize; i++) {
        const correctText = correctTexts ? correctTexts[i] : null
        question.contents.push(new ContentJapanese(beforeTexts[i], afterTexts[i], correctText))
        question.inputValues.push('')
      }
      return question
    }

    /**
     * 英語(手書き)の問題情報インスタンスを生成する
     *
     * @param questionCode 設問コード
     * @param questionNumber 設問番号(連番)
     * @param managementLabel 設問ラベル
     * @param inputSize 入力欄の数
     * @param beforeTexts 入力欄前の固定文字列(配列)
     * @param afterTexts 入力欄後の固定文字列(配列)
     * @param correctTexts 正解文字列(配列)
     * @param isCorrect 正誤結果
     * @returns 問題情報インスタンス
     */
    public static createEnglishQuestion(
      questionCode: string,
      questionNumber: number,
      managementLabel: string | null,
      inputSize: number,
      beforeTexts: (string | null)[],
      afterTexts: (string | null)[],
      correctTexts?: string[],
      isCorrect?: boolean
    ): Question {
      // インスタンス生成
      const question = new Question(
        questionCode,
        questionNumber,
        managementLabel,
        ANSWER_FORMAT_CODE.ENGLISH,
        isCorrect
      )
      // 解答欄の件数分だけ解答情報と入力枠を生成する
      for (let i = 0; i < inputSize; i++) {
        const correctText = correctTexts ? correctTexts[i] : null
        question.contents.push(new ContentEnglish(beforeTexts[i], afterTexts[i], correctText))
        question.inputValues.push('')
      }
      return question
    }

    /**
     * 択一式の問題情報インスタンスを生成する
     *   複数解答欄には対応しない
     *
     * @param questionCode 設問コード
     * @param questionNumber 設問番号(連番)
     * @param managementLabel 設問ラベル
     * @param beforeText 入力欄前の固定文字列
     * @param afterText 入力欄後の固定文字列
     * @param optionValues 選択肢の文字列(配列)
     * @param correctText 正解文字列
     * @param isCorrect 正誤結果
     * @returns 問題情報インスタンス
     */
    public static createSingleChoiceQuestion(
      questionCode: string,
      questionNumber: number,
      managementLabel: string | null,
      beforeText: string | null,
      afterText: string | null,
      optionValues: string[],
      correctText?: string,
      isCorrect?: boolean
    ): Question {
      // インスタンス生成
      const question = new Question(
        questionCode,
        questionNumber,
        managementLabel,
        ANSWER_FORMAT_CODE.SINGLE_CHOICE,
        isCorrect
      )
      // 解答情報と入力枠を1件分生成する
      question.contents.push(new ContentSingleChoice(beforeText, afterText, optionValues, correctText || null))
      question.inputValues.push('')

      return question
    }
  }

  /**
   * 小問情報を保持するクラス
   */
  export class Problem {
    /** 小問コード */
    readonly problemCode: string
    /** 問題画像URL */
    readonly imageDescriptions: string[]
    /** 解説画像URL */
    readonly imageGuidances: string[]
    /** 設問数 */
    questionCount: number
    /** 設問情報 */
    questions: Question[]

    /**
     * コンストラクタ
     *
     * @param problemCode 小問コード
     * @param imageDescriptions 問題画像URL
     * @param imageGuidances 解説画像URL
     */
    constructor(problemCode: string, imageDescriptions: string[], imageGuidances: string[]) {
      this.problemCode = problemCode
      this.imageDescriptions = imageDescriptions
      this.imageGuidances = imageGuidances
      // 設問数と設問情報を初期化
      this.questionCount = 0
      this.questions = []
    }

    /**
     * 例題か否かを判定して返す
     *
     * @returns 問題が例題か否か
     */
    public get isExample(): boolean {
      // 設問に例題が含まれる場合は例題として扱う
      return this.questions.some((question) => {
        return question.answerFormatCode === ANSWER_FORMAT_CODE.EXAMPLE
      })
    }

    /**
     * APIレスポンスから設問解答タイプに応じた設問情報を追加
     *
     * @param responseQuestion 設問情報レスポンス
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    public addQuestionByResponse(responseQuestion: ResponseQuestion, isCorrect?: boolean): void {
      // 例外時のみフラグを落としてエラーを検知可能にする
      let isSuccess = true

      switch (responseQuestion.answerFormatCode) {
        // 例題
        case ANSWER_FORMAT_CODE.EXAMPLE:
          this.addExampleQuestionByResponse(responseQuestion, isCorrect)
          break
        // テキスト入力形式
        case ANSWER_FORMAT_CODE.INPUT:
          this.addInputQuestionByResponse(responseQuestion, isCorrect)
          break
        // 数式形式
        case ANSWER_FORMAT_CODE.NUMERICAL_FORMULA:
          this.addNumericalFormulaQuestionByResponse(responseQuestion, isCorrect)
          break
        // 日本語形式
        case ANSWER_FORMAT_CODE.JAPANESE:
          this.addJapaneseQuestionByResponse(responseQuestion, isCorrect)
          break
        // 英語形式
        case ANSWER_FORMAT_CODE.ENGLISH:
          this.addEnglishQuestionByResponse(responseQuestion, isCorrect)
          break
        // 択一式
        // TODO: N_LMS-700 API実装後に択一式対応
        // case ANSWER_FORMAT_CODE.SINGLE_CHOICE:
        //   this.addSingleChoiceQuestionByResponse(responseQuestion, isCorrect)
        //   break
        // 例外ケース(警告を出しスキップ)
        default:
          Vue.prototype.$logger.error('不正な解答タイプ')
          isSuccess = false
          break
      }

      // インスタンス生成成功時のみ、最後尾の設問にID情報を付加する
      if (isSuccess) {
        this.questions[this.questions.length - 1].id = responseQuestion.id
      }
    }

    /**
     * APIレスポンスから例題の設問情報を追加
     *
     * @param responseQuestion 設問情報レスポンス
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    private addExampleQuestionByResponse(responseQuestion: ResponseQuestion, isCorrect?: boolean): void {
      // 設問情報を追加
      this.addExampleQuestion(responseQuestion.code, isCorrect)
    }

    /**
     * APIレスポンスから入力形式の設問情報を追加
     *
     * @param responseQuestion 設問情報レスポンス
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    private addInputQuestionByResponse(responseQuestion: ResponseQuestion, isCorrect?: boolean): void {
      // 解答欄前後の文字列を単独配列化
      const beforeTexts = responseQuestion.content.map((content) => {
        return (content as ResponseQuestionCorrectInput).beforeText
      })
      const afterTexts = responseQuestion.content.map((content) => {
        return (content as ResponseQuestionCorrectInput).afterText
      })
      // 正答情報の変数定義
      let correctTexts
      if (isCorrect !== undefined) {
        // 解答済みの場合のみ正答情報を設定
        correctTexts = responseQuestion.content.map((content) => {
          return (content as ResponseQuestionCorrectInput).correctInputText || ''
        })
      }

      // 設問情報を追加
      this.addInputQuestion(
        responseQuestion.code,
        responseQuestion.numberLabel,
        responseQuestion.content.length,
        beforeTexts,
        afterTexts,
        correctTexts,
        isCorrect
      )
    }

    /**
     * APIレスポンスから数式形式の設問情報を追加
     *
     * @param responseQuestion 設問情報レスポンス
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    private addNumericalFormulaQuestionByResponse(responseQuestion: ResponseQuestion, isCorrect?: boolean): void {
      // 解答欄前後の文字列を単独配列化
      const beforeTexts = responseQuestion.content.map((content) => {
        return (content as ResponseQuestionCorrectNumericalFormula).beforeText
      })
      const afterTexts = responseQuestion.content.map((content) => {
        return (content as ResponseQuestionCorrectNumericalFormula).afterText
      })
      // 正答情報の変数定義
      let correctTexts
      if (isCorrect !== undefined) {
        // 解答済みの場合のみ正答情報を設定
        correctTexts = responseQuestion.content.map((content) => {
          return (content as ResponseQuestionCorrectNumericalFormula).correctFormula || ''
        })
      }

      // 設問情報を追加
      this.addNumericalFormulaQuestion(
        responseQuestion.code,
        responseQuestion.numberLabel,
        responseQuestion.content.length,
        beforeTexts,
        afterTexts,
        correctTexts,
        isCorrect
      )
    }

    /**
     * APIレスポンスから日本語形式の設問情報を追加
     *
     * @param responseQuestion 設問情報レスポンス
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    private addJapaneseQuestionByResponse(responseQuestion: ResponseQuestion, isCorrect?: boolean): void {
      // 解答欄前後の文字列を単独配列化
      const beforeTexts = responseQuestion.content.map((content) => {
        return (content as ResponseQuestionCorrectJapanese).beforeText
      })
      const afterTexts = responseQuestion.content.map((content) => {
        return (content as ResponseQuestionCorrectJapanese).afterText
      })
      // 正答情報の変数定義
      let correctTexts
      if (isCorrect !== undefined) {
        // 解答済みの場合のみ正答情報を設定
        correctTexts = responseQuestion.content.map((content) => {
          return (content as ResponseQuestionCorrectJapanese).correctInputText || ''
        })
      }

      // 設問情報を追加
      this.addJapaneseQuestion(
        responseQuestion.code,
        responseQuestion.numberLabel,
        responseQuestion.content.length,
        beforeTexts,
        afterTexts,
        correctTexts,
        isCorrect
      )
    }

    /**
     * APIレスポンスから英語形式の設問情報を追加
     *
     * @param responseQuestion 設問情報レスポンス
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    private addEnglishQuestionByResponse(responseQuestion: ResponseQuestion, isCorrect?: boolean): void {
      // 解答欄前後の文字列を単独配列化
      const beforeTexts = responseQuestion.content.map((content) => {
        return (content as ResponseQuestionCorrectEnglish).beforeText
      })
      const afterTexts = responseQuestion.content.map((content) => {
        return (content as ResponseQuestionCorrectEnglish).afterText
      })
      // 正答情報の変数定義
      let correctTexts
      if (isCorrect !== undefined) {
        // 解答済みの場合のみ正答情報を設定
        correctTexts = responseQuestion.content.map((content) => {
          return (content as ResponseQuestionCorrectEnglish).correctInputText || ''
        })
      }

      // 設問情報を追加
      this.addEnglishQuestion(
        responseQuestion.code,
        responseQuestion.numberLabel,
        responseQuestion.content.length,
        beforeTexts,
        afterTexts,
        correctTexts,
        isCorrect
      )
    }

    // TODO: N_LMS-700 API実装後に択一式対応
    //   addSingleChoiceQuestionByResponse の作成

    /**
     * 例題の設問を追加
     *
     * @param questionCode 設問コード
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    public addExampleQuestion(questionCode: string, isCorrect?: boolean): void {
      // 設問情報をインスタンス化
      const question = Question.createExampleQuestion(questionCode, this.questionCount + 1, isCorrect)
      // 設問情報を追加
      this.questions.push(question)
      // 設問数を+1
      this.questionCount++
    }

    /**
     * 入力式の設問を追加
     *
     * @param questionCode 設問コード
     * @param managementLabel 設問ラベル
     * @param inputSize 入力枠の数
     * @param beforeTexts 入力欄前の固定文字列(配列)
     * @param afterTexts 入力欄後の固定文字列(配列)
     * @param correctTexts 正答文字列(配列) 正答情報を使用する画面でのみ設定
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    public addInputQuestion(
      questionCode: string,
      managementLabel: string | null,
      inputSize: number,
      beforeTexts: (string | null)[],
      afterTexts: (string | null)[],
      correctTexts?: string[],
      isCorrect?: boolean
    ): void {
      // 設問情報をインスタンス化
      const question = Question.createInputQuestion(
        questionCode,
        this.questionCount + 1,
        managementLabel,
        inputSize,
        beforeTexts,
        afterTexts,
        correctTexts,
        isCorrect
      )
      // 設問情報を追加
      this.questions.push(question)
      // 設問数を+1
      this.questionCount++
    }

    /**
     * 数式の設問を追加
     *
     * @param questionCode 設問コード
     * @param managementLabel 設問ラベル
     * @param inputSize 入力枠の数
     * @param beforeTexts 入力欄前の固定文字列(配列)
     * @param afterTexts 入力欄後の固定文字列(配列)
     * @param correctTexts 正答文字列(配列) 正答情報を使用する画面でのみ設定
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    public addNumericalFormulaQuestion(
      questionCode: string,
      managementLabel: string | null,
      inputSize: number,
      beforeTexts: (string | null)[],
      afterTexts: (string | null)[],
      correctTexts?: string[],
      isCorrect?: boolean
    ): void {
      // 設問情報をインスタンス化
      const question = Question.createNumericalFormulaQuestion(
        questionCode,
        this.questionCount + 1,
        managementLabel,
        inputSize,
        beforeTexts,
        afterTexts,
        correctTexts,
        isCorrect
      )
      // 設問情報を追加
      this.questions.push(question)
      // 設問数を+1
      this.questionCount++
    }

    /**
     * 日本語(手書き)の設問を追加
     *
     * @param questionCode 設問コード
     * @param managementLabel 設問ラベル
     * @param inputSize 入力枠の数
     * @param beforeTexts 入力欄前の固定文字列(配列)
     * @param afterTexts 入力欄後の固定文字列(配列)
     * @param correctTexts 正答文字列(配列) 正答情報を使用する画面でのみ設定
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    public addJapaneseQuestion(
      questionCode: string,
      managementLabel: string | null,
      inputSize: number,
      beforeTexts: (string | null)[],
      afterTexts: (string | null)[],
      correctTexts?: string[],
      isCorrect?: boolean
    ): void {
      // 設問情報をインスタンス化
      const question = Question.createJapaneseQuestion(
        questionCode,
        this.questionCount + 1,
        managementLabel,
        inputSize,
        beforeTexts,
        afterTexts,
        correctTexts,
        isCorrect
      )
      // 設問情報を追加
      this.questions.push(question)
      // 設問数を+1
      this.questionCount++
    }

    /**
     * 英語(手書き)の設問を追加
     *
     * @param questionCode 設問コード
     * @param managementLabel 設問ラベル
     * @param inputSize 入力枠の数
     * @param beforeTexts 入力欄前の固定文字列(配列)
     * @param afterTexts 入力欄後の固定文字列(配列)
     * @param correctTexts 正答文字列(配列) 正答情報を使用する画面でのみ設定
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    public addEnglishQuestion(
      questionCode: string,
      managementLabel: string | null,
      inputSize: number,
      beforeTexts: (string | null)[],
      afterTexts: (string | null)[],
      correctTexts?: string[],
      isCorrect?: boolean
    ): void {
      // 設問情報をインスタンス化
      const question = Question.createEnglishQuestion(
        questionCode,
        this.questionCount + 1,
        managementLabel,
        inputSize,
        beforeTexts,
        afterTexts,
        correctTexts,
        isCorrect
      )
      // 設問情報を追加
      this.questions.push(question)
      // 設問数を+1
      this.questionCount++
    }

    /**
     * 択一式の設問を追加
     *
     * @param questionCode 設問コード
     * @param managementLabel 設問ラベル
     * @param beforeText 入力欄前の固定文字列
     * @param afterText 入力欄後の固定文字列
     * @param optionValues 選択肢の文字列(配列)
     * @param correctText 正答文字列 正答情報を使用する画面でのみ設定
     * @param isCorrect 正誤結果 解答済みの場合のみ設定
     */
    public addSingleChoiceQuestion(
      questionCode: string,
      managementLabel: string | null,
      beforeText: string | null,
      afterText: string | null,
      optionValues: string[],
      correctText?: string,
      isCorrect?: boolean
    ): void {
      // 設問情報をインスタンス化
      const question = Question.createSingleChoiceQuestion(
        questionCode,
        this.questionCount + 1,
        managementLabel,
        beforeText,
        afterText,
        optionValues,
        correctText,
        isCorrect
      )
      // 設問情報を追加
      this.questions.push(question)
      // 設問数を+1
      this.questionCount++
    }

    /**
     * 入力情報
     *
     * @returns 入力情報(二次元配列)
     */
    public inputValues(): string[][] {
      return this.questions.map((question) => {
        return question.inputValues
      })
    }
  }
}
